import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getShipments } from '../../api/endpoints/admin';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { ShipmentStatus } from '../../api/types';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { shipmentStatusLabel } from '../shipments/shipmentStatusLabel';
import { StatusPill } from '../../components/ui/StatusPill';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { shipmentStatusColor } from '../../components/domain/statusColor';
import { ShipmentFormModal } from '../shipments/ShipmentFormModal';
import { curr, dualPrice, partnerPriceLabel } from '../../utils/price';
import { VehicleCell } from '../../components/domain/VehicleCell';
import styles from './OrdersView.module.css';
import { RouteText } from '../../components/domain/RouteText';
import { ProfitCell } from '../profit/ProfitCell';
import { useCanSeeProfit } from '../profit/hooks';

const STATUS_OPTS: { label: string; value: ShipmentStatus | 'ORDERS' }[] = [
  { label: 'All orders', value: 'ORDERS' },
  { label: 'Posted', value: 'POSTED' },
  { label: 'Draft', value: 'DRAFT' },
];

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function OrdersView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stateRestore = (location.state as {
    page?: number;
    search?: string;
    status?: ShipmentStatus | 'ORDERS';
  } | null);

  const initialPage = searchParams.get('page')
    ? Number(searchParams.get('page'))
    : (stateRestore?.page ?? 0);
  const initialSearch = searchParams.get('search') ?? stateRestore?.search ?? '';
  const initialStatus =
    (searchParams.get('status') as ShipmentStatus | 'ORDERS' | null) ??
    stateRestore?.status ??
    'ORDERS';

  const [page, setPage] = useState(!Number.isNaN(initialPage) ? initialPage : 0);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebouncedValue(search, 300, () => setPage(0));
  // Orders tab is scoped to pre-award shipments — default to both Draft + Posted
  const [status, setStatus] = useState<ShipmentStatus | 'ORDERS'>(initialStatus);
  const [isNewOpen, setIsNewOpen] = useState(false);

  function handleSearch(v: string) {
    setSearch(v);
  }

  const buildReturnUrl = () => {
    const params = new URLSearchParams();
    if (page > 0) params.set('page', String(page));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (status && status !== 'ORDERS') params.set('status', status);
    const qs = params.toString();
    return qs ? `${location.pathname}?${qs}` : location.pathname;
  };

  const navState = {
    from: buildReturnUrl(),
    fromLabel: 'Orders',
    restoreState: { page, search: debouncedSearch, status },
  };

  const queryKey = ['admin', 'orders', page, debouncedSearch, status] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getShipments({
        page,
        size: 25,
        search: debouncedSearch || undefined,
        status,
      }),
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 2),
  });
  const rows = data?.items ?? [];
  const canSeeProfit = useCanSeeProfit();

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <input
          id="orders-search"
          className={styles.search}
          type="search"
          placeholder={t('orders.ordersView.searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <div className={styles.filters}>
          {STATUS_OPTS.map((opt) => (
            <button
              key={opt.value}
              className={status === opt.value ? `${styles.chip} ${styles.chipActive}` : styles.chip}
              onClick={() => {
                setStatus(opt.value);
                setPage(0);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <RefreshButton
          queryKeys={[
            ['admin', 'orders'],
            ['admin', 'dashboard'],
          ]}
        />
        <button className={styles.newBtn} onClick={() => setIsNewOpen(true)}>
          {t('orders.ordersView.newShipment')}
        </button>
      </div>



      <DataTable
        loading={isLoading}
        error={isError ? "We couldn’t load orders" : undefined}
        onRetry={() => refetch()}
        keyFn={(s) => s.id}
        rows={rows}
        onRowClick={(s) => navigate(`/shipments/${s.id}`, { state: navState })}
        empty="No orders match the current filter"
        columns={[
          {
            key: 'id',
            header: 'ID',
            width: '64px',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.detailLink}>
                #{s.id}
              </Link>
            ),
          },
          {
            key: 'route',
            header: 'Route',
            render: (s) => (
              <Link
                to={`/shipments/${s.id}`}
                state={navState}
                className={styles.detailLink}
              >
                <RouteText route={s} />
              </Link>
            ),
          },
          {
            key: 'vehicle',
            header: 'Vehicle',
            width: '140px',
            render: (s) => <VehicleCell assignment={s.assignment} />,
          },
          { key: 'pickup', header: 'Pickup', width: '110px', render: (s) => fmt(s.pickupDate) },
          {
            key: 'delivery',
            header: 'Delivery',
            width: '110px',
            render: (s) => fmt(s.deliveryDate),
          },
          {
            key: 'price',
            header: 'Price',
            width: '110px',
            // Original prominent; margin-adjusted view beneath in muted text
            render: (s) => {
              // #288, R36: before award the partner price (or "—"), never a margin-derived carrier figure.
              if (s.finalPrice == null) {
                return (
                  <span>
                    {curr(s.offeredPrice)}
                    <span className={styles.priceSub}>{partnerPriceLabel(s.partnerOfferPrice)}</span>
                  </span>
                );
              }
              const dual = dualPrice(s.finalPrice, s.shipperFacingFinalPrice);
              return (
                <span>
                  {dual.original}
                  {dual.adjusted && <span className={styles.priceSub}>{t('orders.ordersView.withMargin', { price: dual.adjusted })}</span>}
                </span>
              );
            },
          },
          // #300: per-order profit, demo world and managers only (P9); the server's band and margin, or "—".
          ...(canSeeProfit
            ? [{ key: 'profit', header: 'Profit', width: '170px', render: (s: (typeof rows)[number]) => <ProfitCell profit={s.profit} /> }]
            : []),
          {
            key: 'status',
            header: 'Status',
            width: '140px',
            render: (s) => {
              const c = shipmentStatusColor(s.status);
              return <StatusPill bg={c.bg} text={c.text} label={shipmentStatusLabel(s.status)} title={s.status} />;
            },
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}

      {isNewOpen && (
        <ShipmentFormModal
          onClose={() => setIsNewOpen(false)}
          onSaved={(created) => navigate(`/shipments/${created.id}`, { state: navState })}
        />
      )}
    </div>
  );
}
